// /admin/audit-log — read-only browse of the HMAC-chained audit trail.
//
// Wave 4J polish:
//   - MultiSelect filters for `kind` (== action) and `source` populated by a
//     dedicated `/api/admin/audit-log/kinds` endpoint.
//   - DatePickerInput range filter on `created_at`.
//   - Pretty-printed JSON payload (regex-driven syntax highlight, no extra
//     dependency) inside a Mantine Code block.
//   - Per-row chain-integrity hint — a small green check when the row's
//     `chain_prev_hash` matches the previous row's `chain_hash`. UI signal
//     only; the canonical integrity check is server-side.

import {
  ActionIcon,
  Alert,
  Button,
  Code,
  CopyButton,
  Group,
  Loader,
  MultiSelect,
  Pagination,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { useMutation, useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useMemo, useState } from 'react';

import { AdminShell } from '../../components/admin-shell/AdminShell';
import { adminGet, adminPost } from './_api';

type AuditRow = {
  id: string;
  actor_user_id: string | null;
  actor_kind: 'user' | 'system' | 'admin';
  target_kind: string;
  target_id: string | null;
  action: string;
  source: string | null;
  payload: Record<string, unknown> | null;
  created_at: string;
  chain_seq: number;
  chain_prev_hash: string | null;
  chain_hash: string;
  idempotency_key: string | null;
};

type AuditList = {
  items: AuditRow[];
  total: number;
};

type KindsResponse = {
  kinds: string[];
  sources: string[];
};

type AuditRowSummary = {
  id: string;
  chain_seq: number;
  chain_prev_hash: string | null;
  chain_hash: string;
  actor_user_id: string | null;
  actor_kind: 'user' | 'system' | 'admin';
  target_kind: string;
  target_id: string | null;
  action: string;
  source: string | null;
  created_at: string;
};

type VerifyBreak = {
  seq: number;
  id: string;
  expectedHash: string;
  actualHash: string;
  row: AuditRowSummary;
  prevRow: AuditRowSummary | null;
};

type VerifyResponse =
  | { ok: true; rowsChecked: number; lastSeq: number; firstBreak: null }
  | { ok: false; rowsChecked: number; lastSeq: number; firstBreak: VerifyBreak };

const PAGE_SIZE = 50;

export const Route = createFileRoute('/admin/audit-log')({
  component: AdminAuditLog,
});

function AdminAuditLog() {
  const [actor, setActor] = useState('');
  const [targetKind, setTargetKind] = useState('');
  const [targetId, setTargetId] = useState('');
  const [kinds, setKinds] = useState<string[]>([]);
  const [sources, setSources] = useState<string[]>([]);
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([null, null]);
  const [page, setPage] = useState(1);
  const offset = (page - 1) * PAGE_SIZE;

  const params = new URLSearchParams();
  if (actor.trim()) params.set('actor_user_id', actor.trim());
  if (targetKind.trim()) params.set('target_kind', targetKind.trim());
  if (targetId.trim()) params.set('target_id', targetId.trim());
  for (const k of kinds) params.append('kind', k);
  for (const s of sources) params.append('source', s);
  if (dateRange[0]) params.set('from', isoDate(dateRange[0]));
  if (dateRange[1]) params.set('to', isoDate(dateRange[1]));
  params.set('limit', String(PAGE_SIZE));
  params.set('offset', String(offset));

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'audit', actor, targetKind, targetId, kinds, sources, dateRange, page],
    queryFn: () => adminGet<AuditList>(`/api/admin/audit-log?${params.toString()}`),
  });

  // Distinct values for the dropdowns. Stable URL, cached for the session.
  const { data: kindsData } = useQuery({
    queryKey: ['admin', 'audit-kinds'],
    queryFn: () => adminGet<KindsResponse>('/api/admin/audit-log/kinds'),
    staleTime: 60_000,
  });

  // End-to-end chain verifier. Walks the chain from chain_seq=1 server-side and
  // reports the first break (or `ok: true` for a clean chain). The verify can
  // take seconds for a large log, so we drive it through a Mantine Button with
  // an explicit loading state and surface the outcome in an Alert.
  const verifyMutation = useMutation({
    mutationFn: () => adminPost<VerifyResponse>('/api/admin/audit-log/verify', {}),
  });

  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;

  // Walk the page once and mark each row's chain integrity by comparing its
  // `chain_prev_hash` to the previous row's `chain_hash`. Rows are sorted DESC
  // by chain_seq, so "previous in time" is the NEXT row in this list.
  const integrityById = useMemo(() => {
    const m = new Map<string, 'ok' | 'mismatch' | 'unknown'>();
    const items = data?.items ?? [];
    for (let i = 0; i < items.length; i++) {
      const row = items[i];
      const prev = items[i + 1]; // next in array = older in time
      if (!row) continue;
      if (!prev) {
        m.set(row.id, 'unknown'); // last row in the page; no anchor in view
        continue;
      }
      m.set(row.id, row.chain_prev_hash === prev.chain_hash ? 'ok' : 'mismatch');
    }
    return m;
  }, [data]);

  return (
    <AdminShell>
      <Stack gap="md">
        <Title order={2}>Audit log</Title>
        <Text c="dimmed" size="sm">
          The append-only HMAC-chained trail of every state-machine transition and admin action.
          Use <Code>kind</Code> and <Code>source</Code> to narrow; the date range scopes to
          row creation time.
        </Text>

        <Group gap="sm" align="center">
          <Button
            variant="light"
            color="brand"
            onClick={() => verifyMutation.mutate()}
            loading={verifyMutation.isPending}
            disabled={verifyMutation.isPending}
          >
            {verifyMutation.isPending ? 'Verifying…' : 'Verify chain integrity'}
          </Button>
          <Text size="xs" c="dimmed">
            Walks every row from seq 1 and recomputes each HMAC.
          </Text>
        </Group>

        {verifyMutation.isError ? (
          <Alert color="red" title="Verification failed to run" variant="light">
            <Text size="sm">
              {verifyMutation.error instanceof Error
                ? verifyMutation.error.message
                : 'Unknown error'}
            </Text>
          </Alert>
        ) : null}

        {verifyMutation.data && verifyMutation.data.ok ? (
          <Alert color="tide" title="Chain verified" variant="light">
            <Text size="sm">
              Chain verified. {verifyMutation.data.rowsChecked} rows checked through seq{' '}
              {verifyMutation.data.lastSeq}.
            </Text>
          </Alert>
        ) : null}

        {verifyMutation.data && !verifyMutation.data.ok ? (
          <ChainBreakAlert result={verifyMutation.data} />
        ) : null}

        <Group gap="sm" wrap="wrap" align="flex-end">
          <MultiSelect
            label="Kind"
            placeholder="Any kind"
            data={kindsData?.kinds ?? []}
            value={kinds}
            onChange={(v) => {
              setKinds(v);
              setPage(1);
            }}
            searchable
            clearable
            style={{ minWidth: 240 }}
          />
          <MultiSelect
            label="Source"
            placeholder="Any source"
            data={kindsData?.sources ?? []}
            value={sources}
            onChange={(v) => {
              setSources(v);
              setPage(1);
            }}
            searchable
            clearable
            style={{ minWidth: 220 }}
          />
          <DatePickerInput
            type="range"
            label="Date range"
            placeholder="Pick range"
            value={dateRange}
            onChange={(range) => {
              const [s, e] = range;
              const sDate = s !== null ? new Date(s) : null;
              const eDate = e !== null ? new Date(e) : null;
              setDateRange([sDate, eDate]);
              setPage(1);
            }}
            style={{ minWidth: 240 }}
          />
          <TextInput
            label="Actor user id"
            value={actor}
            onChange={(e) => {
              setActor(e.currentTarget.value);
              setPage(1);
            }}
            style={{ minWidth: 200 }}
          />
          <TextInput
            label="Target kind"
            value={targetKind}
            onChange={(e) => {
              setTargetKind(e.currentTarget.value);
              setPage(1);
            }}
            style={{ minWidth: 160 }}
          />
          <TextInput
            label="Target id"
            value={targetId}
            onChange={(e) => {
              setTargetId(e.currentTarget.value);
              setPage(1);
            }}
            style={{ minWidth: 200 }}
          />
        </Group>

        {isLoading ? (
          <Group>
            <Loader size="sm" />
            <Text c="dimmed">Loading…</Text>
          </Group>
        ) : (
          <>
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>When</Table.Th>
                  <Table.Th>Chain</Table.Th>
                  <Table.Th>Actor</Table.Th>
                  <Table.Th>Kind</Table.Th>
                  <Table.Th>Source</Table.Th>
                  <Table.Th>Target</Table.Th>
                  <Table.Th>Payload</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {(data?.items ?? []).map((r) => (
                  <Table.Tr key={r.id}>
                    <Table.Td>
                      <Text size="xs">{formatDate(r.created_at)}</Text>
                    </Table.Td>
                    <Table.Td>
                      <ChainHint
                        seq={r.chain_seq}
                        integrity={integrityById.get(r.id) ?? 'unknown'}
                      />
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs" c={r.actor_kind === 'system' ? 'dimmed' : 'inherit'}>
                        {r.actor_kind}
                        {r.actor_user_id ? ` · ${r.actor_user_id.slice(0, 8)}…` : ''}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Code>{r.action}</Code>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs" c="dimmed">
                        {r.source ?? '–'}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs">
                        {r.target_kind}
                        {r.target_id ? ` · ${r.target_id.slice(0, 8)}…` : ''}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <PayloadCell payload={r.payload} />
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
            {(data?.items.length ?? 0) === 0 ? (
              <Text c="dimmed">No audit rows match those filters.</Text>
            ) : null}
            <Group justify="space-between">
              <Text c="dimmed" size="sm">
                {data?.total ?? 0} total
              </Text>
              <Pagination value={page} onChange={setPage} total={totalPages} />
            </Group>
          </>
        )}
      </Stack>
    </AdminShell>
  );
}

/**
 * ChainBreakAlert — rendered when the verifier finds a break. Surfaces the
 * expected vs actual hash with copy-to-clipboard buttons and shows the
 * surrounding row IDs so an admin can paste straight into an incident report.
 */
function ChainBreakAlert({
  result,
}: {
  result: { ok: false; rowsChecked: number; lastSeq: number; firstBreak: VerifyBreak };
}) {
  const { firstBreak, rowsChecked } = result;
  return (
    <Alert color="red" title="Chain break detected" variant="light">
      <Stack gap="xs">
        <Text size="sm" fw={500}>
          Chain break detected at seq {firstBreak.seq}. {rowsChecked} rows checked before the break.
        </Text>
        <HashRow label="Expected hash" value={firstBreak.expectedHash} />
        <HashRow label="Actual hash" value={firstBreak.actualHash} />
        <Group gap="xs" wrap="wrap">
          <Text size="xs" c="dimmed">Broken row id:</Text>
          <Code>{firstBreak.row.id}</Code>
          <CopyButton value={firstBreak.row.id} timeout={1500}>
            {({ copied, copy }) => (
              <Tooltip label={copied ? 'Copied' : 'Copy id'} withArrow>
                <ActionIcon
                  size="xs"
                  variant="subtle"
                  color={copied ? 'tide' : 'gray'}
                  onClick={copy}
                  aria-label="Copy broken row id"
                >
                  {copied ? '✓' : '⧉'}
                </ActionIcon>
              </Tooltip>
            )}
          </CopyButton>
        </Group>
        {firstBreak.prevRow ? (
          <Group gap="xs" wrap="wrap">
            <Text size="xs" c="dimmed">Previous row id (seq {firstBreak.prevRow.chain_seq}):</Text>
            <Code>{firstBreak.prevRow.id}</Code>
            <CopyButton value={firstBreak.prevRow.id} timeout={1500}>
              {({ copied, copy }) => (
                <Tooltip label={copied ? 'Copied' : 'Copy id'} withArrow>
                  <ActionIcon
                    size="xs"
                    variant="subtle"
                    color={copied ? 'tide' : 'gray'}
                    onClick={copy}
                    aria-label="Copy previous row id"
                  >
                    {copied ? '✓' : '⧉'}
                  </ActionIcon>
                </Tooltip>
              )}
            </CopyButton>
          </Group>
        ) : (
          <Text size="xs" c="dimmed">
            Break occurred on the first row. No prior row in scope.
          </Text>
        )}
        <Text size="xs" c="dimmed">
          Full break record:
        </Text>
        <Code
          block
          style={{
            whiteSpace: 'pre-wrap',
            maxHeight: 280,
            overflow: 'auto',
            fontSize: 11,
            lineHeight: 1.5,
          }}
        >
          {JSON.stringify(firstBreak, null, 2)}
        </Code>
      </Stack>
    </Alert>
  );
}

function HashRow({ label, value }: { label: string; value: string }) {
  return (
    <Group gap="xs" wrap="nowrap" align="center">
      <Text size="xs" c="dimmed" style={{ minWidth: 110 }}>
        {label}:
      </Text>
      <Code style={{ wordBreak: 'break-all', flex: 1 }}>{value}</Code>
      <CopyButton value={value} timeout={1500}>
        {({ copied, copy }) => (
          <Tooltip label={copied ? 'Copied' : `Copy ${label.toLowerCase()}`} withArrow>
            <ActionIcon
              size="xs"
              variant="subtle"
              color={copied ? 'tide' : 'gray'}
              onClick={copy}
              aria-label={`Copy ${label.toLowerCase()}`}
            >
              {copied ? '✓' : '⧉'}
            </ActionIcon>
          </Tooltip>
        )}
      </CopyButton>
    </Group>
  );
}

function ChainHint({
  seq,
  integrity,
}: {
  seq: number;
  integrity: 'ok' | 'mismatch' | 'unknown';
}) {
  const tooltip =
    integrity === 'ok'
      ? `Chain link OK: seq ${seq}`
      : integrity === 'mismatch'
        ? `Chain mismatch: seq ${seq}`
        : `Seq ${seq}: anchor not in view`;
  const color =
    integrity === 'ok' ? 'var(--mantine-color-tide-6)'
    : integrity === 'mismatch' ? 'var(--mantine-color-red-6)'
    : 'var(--mantine-color-gray-5)';
  const glyph = integrity === 'ok' ? '✓' : integrity === 'mismatch' ? '✕' : '·';
  return (
    <Tooltip label={tooltip} withArrow>
      <Text size="xs" component="span" style={{ color, fontFamily: 'monospace' }}>
        {glyph} {seq}
      </Text>
    </Tooltip>
  );
}

function PayloadCell({ payload }: { payload: Record<string, unknown> | null }) {
  if (payload === null || Object.keys(payload).length === 0) {
    return <Text size="xs" c="dimmed">–</Text>;
  }
  // Hide the chain meta from the inline rendering — it's structural noise. The
  // chain hint column already surfaces the integrity signal.
  const display: Record<string, unknown> = { ...payload };
  delete display['chain'];
  const json = JSON.stringify(display, null, 2);
  return (
    <Code
      block
      style={{
        whiteSpace: 'pre-wrap',
        maxWidth: 480,
        maxHeight: 220,
        overflow: 'auto',
        fontSize: 11,
        lineHeight: 1.5,
      }}
    >
      <span dangerouslySetInnerHTML={{ __html: highlightJson(json) }} />
    </Code>
  );
}

/**
 * Tiny syntax highlighter for JSON. Walks the canonical-formatted output and
 * wraps tokens in <span> tags styled inline. Deliberately avoids an external
 * dependency — the regexes are small enough to audit, and the input is
 * already JSON.stringify'd so it can't contain unescaped HTML.
 */
function highlightJson(json: string): string {
  // First escape HTML-special chars in the source string. JSON.stringify
  // already escapes <, >, & inside strings, but the structural tokens don't
  // need escaping — the only risk is in the user-provided string values,
  // which JSON.stringify escapes via \u sequences. Belt-and-braces below.
  const escaped = json
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  // Order matters: keys (with trailing colon) before generic strings, then
  // numbers, then booleans/null. The regex anchors on the JSON shape so it
  // doesn't accidentally re-paint already-wrapped spans (no recursion).
  return escaped.replace(
    /("(?:\\.|[^"\\])*")(\s*:)?|\b(true|false|null)\b|(-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?)/g,
    (match, str, colon, lit, num) => {
      if (str !== undefined) {
        if (colon !== undefined) {
          return `<span style="color: var(--mantine-color-brand-7)">${str}</span>${colon}`;
        }
        return `<span style="color: var(--mantine-color-tide-7)">${str}</span>`;
      }
      if (lit !== undefined) {
        return `<span style="color: var(--mantine-color-sun-7)">${lit}</span>`;
      }
      if (num !== undefined) {
        return `<span style="color: var(--mantine-color-brand-9)">${num}</span>`;
      }
      return match;
    },
  );
}

function isoDate(d: Date): string {
  const y = d.getFullYear().toString().padStart(4, '0');
  const m = (d.getMonth() + 1).toString().padStart(2, '0');
  const day = d.getDate().toString().padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString();
}
